import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PagedList } from '../models/paged-list.interface';
import { RequestModel } from '../models/request-model.interface';
import { CompanyInvoice as CompanyInvoiceListItem } from '../models/sales/company-invoice.interface';
import { CompanyInvoiceDetails } from '../models/sales/company-invoice-details.interface';
import {
  CreateCompanyInvoiceRequest,
  UpdateCompanyInvoiceRequest,
} from '../models/sales/create-company-invoice-request.interface';
import { CreateInvoiceRequest } from '../models/sales/create-invoice-request.interface';
import { CompanyInvoice } from '../models/sales/invoice.interface';

/**
 * Shape of the structured error body returned by InvoicesController. Present on every
 * domain 400 (`{ code, message }` where `code` is one of `INVOICE_*`) and on every domain
 * 404 (`code` is `SALES_ORDER_NOT_FOUND` or `INVOICE_NOT_FOUND`).
 *
 * Fields are optional because non-domain failures (auth, network, generic 4xx, plus the
 * legacy fallback 404 arm that may surface a bare `{ message }` for non-invoice-specific
 * EntityNotFoundException paths) may surface as an HttpErrorResponse without the full
 * envelope.
 */
export interface InvoiceErrorBody {
  code?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class InvoiceService {
  private readonly httpClient = inject(HttpClient);

  // -- Linked-mode endpoints (per-order invoices tab) ----------------------------------------

  public createInvoice(
    companyId: string,
    salesOrderId: string,
    request: CreateInvoiceRequest,
  ): Observable<CompanyInvoice> {
    return this.httpClient.post<CompanyInvoice>(
      this.getOrderInvoicesUrl(companyId, salesOrderId),
      request,
    );
  }

  public getInvoices(companyId: string, salesOrderId: string): Observable<CompanyInvoice[]> {
    return this.httpClient.get<CompanyInvoice[]>(
      this.getOrderInvoicesUrl(companyId, salesOrderId),
    );
  }

  public getInvoice(companyId: string, invoiceId: string): Observable<CompanyInvoice> {
    return this.httpClient.get<CompanyInvoice>(this.getInvoiceUrl(companyId, invoiceId));
  }

  // -- Top-level company-wide invoice endpoints ----------------------------------------------

  /**
   * `POST /api/companies/{companyId}/invoices/paged` — paged list for the top-level invoices
   * page. Mirrors the sales-orders paging convention.
   */
  public getCompanyInvoicesPaged(
    companyId: string,
    request: RequestModel,
  ): Observable<PagedList<CompanyInvoiceListItem>> {
    return this.httpClient.post<PagedList<CompanyInvoiceListItem>>(
      `${this.getCompanyInvoicesUrl(companyId)}/paged`,
      request,
    );
  }

  /** `GET /api/companies/{companyId}/invoices` — full list (no paging). */
  public getCompanyInvoices(companyId: string): Observable<CompanyInvoiceListItem[]> {
    return this.httpClient.get<CompanyInvoiceListItem[]>(this.getCompanyInvoicesUrl(companyId));
  }

  /**
   * `GET /api/companies/{companyId}/invoices/{invoiceId}` — full detail with nullable
   * `salesOrderId` (an invoice may be standalone or linked to a sales order).
   */
  public getCompanyInvoiceById(
    companyId: string,
    invoiceId: string,
  ): Observable<CompanyInvoiceDetails> {
    return this.httpClient.get<CompanyInvoiceDetails>(this.getInvoiceUrl(companyId, invoiceId));
  }

  /** `POST /api/companies/{companyId}/invoices` — standalone or linked create. */
  public createCompanyInvoice(
    companyId: string,
    body: CreateCompanyInvoiceRequest,
  ): Observable<CompanyInvoiceDetails> {
    return this.httpClient.post<CompanyInvoiceDetails>(
      this.getCompanyInvoicesUrl(companyId),
      body,
    );
  }

  /** `PATCH /api/companies/{companyId}/invoices/{invoiceId}`. */
  public updateCompanyInvoice(
    companyId: string,
    invoiceId: string,
    body: UpdateCompanyInvoiceRequest,
  ): Observable<CompanyInvoiceDetails> {
    return this.httpClient.patch<CompanyInvoiceDetails>(
      this.getInvoiceUrl(companyId, invoiceId),
      body,
    );
  }

  /** Convenience wrapper for `PATCH { status: 'Paid' }`. Forward-only per §12 q8. */
  public markCompanyInvoicePaid(
    companyId: string,
    invoiceId: string,
  ): Observable<CompanyInvoiceDetails> {
    return this.updateCompanyInvoice(companyId, invoiceId, { status: 'Paid' });
  }

  /** `DELETE /api/companies/{companyId}/invoices/{invoiceId}` — only allowed while `Open`. */
  public deleteCompanyInvoice(companyId: string, invoiceId: string): Observable<void> {
    return this.httpClient.delete<void>(this.getInvoiceUrl(companyId, invoiceId));
  }

  // -- url helpers ----------------------------------------------------------------------------

  private getOrderInvoicesUrl(companyId: string, salesOrderId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/sales-orders/${salesOrderId}/invoices`;
  }

  private getCompanyInvoicesUrl(companyId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/invoices`;
  }

  private getInvoiceUrl(companyId: string, invoiceId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/invoices/${invoiceId}`;
  }
}
